import React, { useEffect, useMemo } from 'react';
import { get } from 'lodash';
import { SelectOption } from 'global';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Checkbox, MenuItem, Select, SelectChangeEvent, FormControl } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { SELECT_ALL_VALUE } from '../constants';
import { BaseFilterComponentProps } from './types';

interface MultiSelectFilterProps<TableEntity> extends BaseFilterComponentProps<TableEntity, string[]> {
  defaultFilter?: { value: string[] };
  labelFormatter?: (label: string) => string;
}

export const MultiSelectFilter = <TableEntity,>({
  rows,
  column,
  filter,
  onChange,
  defaultFilter,
  labelFormatter,
}: MultiSelectFilterProps<TableEntity>) => {
  const options = useMemo(
    () =>
      rows.reduce<SelectOption[]>((acc, row) => {
        const valueToAdd = get(row, column?.id ?? '');

        if (valueToAdd && !acc.map(({ value }) => value).includes(valueToAdd)) {
          acc.push({
            value: valueToAdd,
            label: labelFormatter ? labelFormatter(valueToAdd) : valueToAdd,
          });
        }

        return acc;
      }, []),
    [rows, column?.id, labelFormatter]
  );

  useEffect(() => {
    if (defaultFilter) {
      onChange(defaultFilter.value);
    }
  }, []);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const nextValue = event.target.value as string[];

    const isAllAfterOther =
      nextValue.includes(SELECT_ALL_VALUE) &&
      nextValue.length > 1 &&
      nextValue[nextValue.length - 1] === SELECT_ALL_VALUE;
    const isOtherAfterAll = nextValue.includes(SELECT_ALL_VALUE) && nextValue.length > 1;

    let newSelected: string[];

    switch (true) {
      case isAllAfterOther:
        newSelected = [SELECT_ALL_VALUE];
        break;
      case isOtherAfterAll:
        newSelected = nextValue.filter(value => value !== SELECT_ALL_VALUE);
        break;
      default:
        newSelected = nextValue;
    }

    onChange(newSelected);
  };

  return (
    <FormControl fullWidth size="small">
      <Select
        multiple
        displayEmpty
        onChange={handleChange}
        disabled={!options.length}
        value={filter?.value ?? [SELECT_ALL_VALUE]}
        renderValue={selected => selected.map(value => (labelFormatter ? labelFormatter(value) : value)).join(', ')}
        MenuProps={{
          style: { maxHeight: '500px' },
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        }}
      >
        <MenuItem key="select-all" value={SELECT_ALL_VALUE}>
          <Checkbox
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checked={!filter?.value || !!filter?.value?.includes(SELECT_ALL_VALUE)}
          />
          Select All
        </MenuItem>

        {options.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            <Checkbox
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checked={!!filter?.value?.includes(value)}
            />
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
